import type { z } from 'zod';

import { isOffEverywhere, mapValues } from '../../compact';
import type { Device, Responsive } from '../../utils';
import { createVersionedFeature } from '../create-versioned-feature';
import {
    backgroundFileSchema,
    backgroundFilterSchema,
    backgroundOverlaySchema,
    backgroundScrollEffectSchema,
    backgroundV0Schema,
} from './v0';

export const DEFAULT_BACKGROUND_COLOR = 'rgba(255, 255, 255, 0)';
export const DEFAULT_BACKGROUND_OVERLAY_COLOR = '#ffffff';
export const DEFAULT_BACKGROUND_OVERLAY_OPACITY = 0.5;
/** `EFilerOverlay.BLURRY` / `EScrollEffect.FIXED` — CSS literals on the host enums. */
export const DEFAULT_BACKGROUND_FILTER = 'blur(3px)';
export const DEFAULT_BACKGROUND_SCROLL_EFFECT = 'fixed';

export type BackgroundSettings = z.output<typeof backgroundV0Schema>;

export type CreateBackgroundOptions = {
    /** `TRANSPARENT_VALUE` — the host's colour-picker preset, rather than a copy of it here. */
    transparentColor?: string;
    /** Seed the block's two extras, `video` and `scrollEffect`. */
    withBlockLayers?: boolean;
};

/** A fresh background: transparent, no image, every layer off. */
export const createBackground = (options?: CreateBackgroundOptions): BackgroundSettings => ({
    version: 0,
    color: { desktop: options?.transparentColor ?? DEFAULT_BACKGROUND_COLOR },
    image: { desktop: null },
    size: { desktop: null },
    position: { desktop: null },
    repeat: { desktop: null },
    overlay: { isEnabled: { desktop: false } },
    filter: { isEnabled: { desktop: false } },
    ...(options?.withBlockLayers ? { video: { desktop: null }, scrollEffect: { isEnabled: { desktop: false } } } : {}),
});

/** The values a layer gets when it is switched on without its own. */
export const BACKGROUND_LAYER_SEEDS = {
    overlay: { color: DEFAULT_BACKGROUND_OVERLAY_COLOR, opacity: DEFAULT_BACKGROUND_OVERLAY_OPACITY },
    filter: { value: DEFAULT_BACKGROUND_FILTER },
    scrollEffect: { value: DEFAULT_BACKGROUND_SCROLL_EFFECT },
} as const;

/**
 * One breakpoint of a background as a plain value — what the image picker edits. The colour and the
 * video have pickers of their own and are not in it.
 */
export type BackgroundAtDevice = {
    image: BackgroundFile | null;
    size: string | null;
    position: string | null;
    repeat: string | null;
    overlay: { isEnabled: boolean; color: string; opacity: number };
    filter: { isEnabled: boolean; value: string };
    scrollEffect: { isEnabled: boolean; value: string };
};

/**
 * A breakpoint's own value, desktop's only where it stores none. `null` is its own: removing the image
 * on a tab writes it, and so does a video replacing it — read as desktop's, the image would come back
 * beside the video and lock both switches. A layer's seed stands in for a value a switched-off layer
 * no longer stores.
 */
export const backgroundAtDevice = (
    background: Partial<BackgroundSettings> | null | undefined,
    device: Device
): BackgroundAtDevice => {
    const { overlay, filter, scrollEffect } = background ?? {};
    const at = <T>(responsive: Responsive<T> | undefined): T | undefined =>
        responsive?.[device] !== undefined ? responsive[device] : responsive?.desktop;

    return {
        image: at(background?.image) ?? null,
        size: at(background?.size) ?? null,
        position: at(background?.position) ?? null,
        repeat: at(background?.repeat) ?? null,
        overlay: {
            isEnabled: Boolean(at(overlay?.isEnabled)),
            color: at(overlay?.color) ?? BACKGROUND_LAYER_SEEDS.overlay.color,
            opacity: at(overlay?.opacity) ?? BACKGROUND_LAYER_SEEDS.overlay.opacity,
        },
        filter: {
            isEnabled: Boolean(at(filter?.isEnabled)),
            value: at(filter?.value) ?? BACKGROUND_LAYER_SEEDS.filter.value,
        },
        scrollEffect: {
            isEnabled: Boolean(at(scrollEffect?.isEnabled)),
            value: at(scrollEffect?.value) ?? BACKGROUND_LAYER_SEEDS.scrollEffect.value,
        },
    };
};

/**
 * The video a breakpoint plays. As with the image, `null` is the breakpoint's own "no video" — what
 * switching it off on a tab writes, and what a legacy device bag without one meant — so only a
 * breakpoint that stores nothing plays desktop's.
 */
export const backgroundVideoAtDevice = (
    background: Partial<BackgroundSettings> | null | undefined,
    device: Device
): BackgroundFile | null => {
    const video = background?.video;

    return (video?.[device] !== undefined ? video[device] : video?.desktop) ?? null;
};

type BackgroundLayer = { isEnabled: Responsive<boolean> } & Record<string, unknown>;

const compactLayer = (layer: unknown) => {
    const bag = layer as BackgroundLayer | undefined;

    return bag && isOffEverywhere(bag.isEnabled) && Object.keys(bag).length > 1 ? { isEnabled: bag.isEnabled } : layer;
};

const LAYERS = new Set(['overlay', 'filter', 'scrollEffect']);

/**
 * Keeps only the switch of a layer that is off at every breakpoint — nothing
 * reads its values. Hands back the same object when there is nothing to drop.
 */
export const compactBackground = <T extends Partial<BackgroundSettings>>(background: T): T =>
    mapValues(background, (layer, key) => (LAYERS.has(key) ? compactLayer(layer) : layer));

/**
 * What a box paints behind its contents.
 *
 * Shared by the **block** and the **column**, which paint the same layer — a
 * colour, a picked image and its layout, a tint and a filter over it. The block
 * adds a video and a per-breakpoint scroll effect. The header **section** has a
 * background too, but a flat one — one for every screen — so it is not this.
 */
export const backgroundFeature = createVersionedFeature({
    versionMap: {
        0: { schema: backgroundV0Schema },
    },
    latestVersion: 0,
    createDefault: () => createBackground(),
});

export type BackgroundFile = z.output<typeof backgroundFileSchema>;
export type BackgroundOverlay = z.output<typeof backgroundOverlaySchema>;
export type BackgroundFilter = z.output<typeof backgroundFilterSchema>;
export type BackgroundScrollEffect = z.output<typeof backgroundScrollEffectSchema>;

export {
    backgroundV0Schema,
    backgroundFileSchema,
    backgroundOverlaySchema,
    backgroundFilterSchema,
    backgroundScrollEffectSchema,
};

export {
    backgroundVar,
    backgroundVars,
    backgroundLayerVar,
    backgroundFilterVar,
    backgroundMarginVar,
    backgroundTintLayer,
    backgroundScrollEffectMode,
    toRgbaColor,
} from './background.css-vars';
